/**
 * MeasurementsScreen — calculadora antropométrica para el entrenador.
 *
 * Calcula al instante (sin botón) IMC, TMB Mifflin-St Jeor, TDEE, calorías
 * objetivo, % grasa US Navy y ratio cintura/cadera. Las salidas se muestran
 * en cards con clasificación cualitativa.
 *
 * Desde B1, soporta HERENCIA DE CLIENTE:
 *   - Dropdown "Calculando para:" arriba del form.
 *   - Al seleccionar un cliente, se pre-rellenan los campos coincidentes
 *     (sex, age desde birthdate, height, peso, cintura, cuello, cadera,
 *     activity_level, goal). Si el coach modifica algún campo, aparece
 *     botón "Guardar cambios al cliente" que llama PUT /clients/:id.
 *   - Si no se selecciona ningún cliente, todo manual como antes.
 *
 * Fórmulas:
 *   - IMC = peso / (estatura/100)²
 *   - TMB H: 10·peso + 6.25·estatura − 5·edad + 5
 *   - TMB M: 10·peso + 6.25·estatura − 5·edad − 161
 *   - TDEE = TMB × factor (1.2 / 1.375 / 1.55 / 1.725 / 1.9)
 *   - Cal objetivo: TDEE − 400 (déficit), TDEE + 300 (superávit)
 *   - % grasa US Navy (Hodgdon-Beckett, fórmula en cm):
 *       H: 495 / (1.0324 − 0.19077·log10(cintura − cuello) + 0.15456·log10(estatura)) − 450
 *       M: 495 / (1.29579 − 0.35004·log10(cintura + cadera − cuello) + 0.22100·log10(estatura)) − 450
 *     IMPORTANTE: NO usar la fórmula imperial (86.01·log10(...) + 36.76) con inputs en cm
 *     — da ~50% para mujer normal en lugar de ~23%. Bug histórico 2026-05-28.
 *   - Ratio cintura/cadera + clasificación de riesgo OMS
 */
import { useEffect, useMemo, useState } from "react";
import { useToast } from "../lib/toast";
import { Icon } from "../lib/Icon";
import {
  listClients,
  updateClient,
  type ClientFull,
  type ClientActivityLevel,
  type ClientGoal,
} from "../api/clients";

type Sex = "M" | "F";
type Activity = "sedentario" | "ligero" | "moderado" | "intenso" | "atleta";
type Goal = "mantener" | "deficit" | "superavit";

interface Inputs {
  sex: Sex;
  age: string;
  weight: string;
  height: string;
  waist: string;
  hip: string;
  neck: string;
  activity: Activity;
  goal: Goal;
}

const ACTIVITY_FACTOR: Record<Activity, number> = {
  sedentario: 1.2,
  ligero: 1.375,
  moderado: 1.55,
  intenso: 1.725,
  atleta: 1.9,
};

const ACTIVITY_LABEL: Record<Activity, string> = {
  sedentario: "Sedentario",
  ligero: "Ligero (1-3 días/sem)",
  moderado: "Moderado (3-5 días/sem)",
  intenso: "Intenso (6-7 días/sem)",
  atleta: "Atleta (2x/día)",
};

// Mapeo enum cliente (inglés) ↔ enum local pantalla (español heredado).
const ACTIVITY_FROM_CLIENT: Record<ClientActivityLevel, Activity> = {
  sedentary: "sedentario",
  light: "ligero",
  moderate: "moderado",
  intense: "intenso",
  athlete: "atleta",
};
const ACTIVITY_TO_CLIENT: Record<Activity, ClientActivityLevel> = {
  sedentario: "sedentary",
  ligero: "light",
  moderado: "moderate",
  intenso: "intense",
  atleta: "athlete",
};
const GOAL_FROM_CLIENT: Record<ClientGoal, Goal> = {
  maintain: "mantener",
  deficit: "deficit",
  surplus: "superavit",
  // recomp no tiene equivalente local — lo mapeamos a "mantener" como aproximación.
  recomp: "mantener",
};
const GOAL_TO_CLIENT: Record<Goal, ClientGoal> = {
  mantener: "maintain",
  deficit: "deficit",
  superavit: "surplus",
};

const DEFAULTS: Inputs = {
  sex: "M",
  age: "",
  weight: "",
  height: "",
  waist: "",
  hip: "",
  neck: "",
  activity: "moderado",
  goal: "mantener",
};

function num(s: string): number | null {
  const n = parseFloat((s || "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function ageFromBirthdate(iso: string | null): string {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const years = (Date.now() - t) / (1000 * 60 * 60 * 24 * 365.25);
  if (years < 0 || years > 150) return "";
  return String(Math.floor(years));
}

/** Construye los inputs desde un ClientFull (campos faltantes quedan vacíos). */
function inputsFromClient(c: ClientFull): Inputs {
  return {
    sex: (c.sex as Sex) || "M",
    age: ageFromBirthdate(c.birthdate),
    weight: c.current_weight_kg != null ? String(c.current_weight_kg) : "",
    height: c.height_cm != null ? String(c.height_cm) : "",
    waist: c.waist_cm != null ? String(c.waist_cm) : "",
    hip: c.hip_cm != null ? String(c.hip_cm) : "",
    neck: c.neck_cm != null ? String(c.neck_cm) : "",
    activity: c.activity_level ? ACTIVITY_FROM_CLIENT[c.activity_level] || "moderado" : "moderado",
    goal: c.goal ? GOAL_FROM_CLIENT[c.goal] || "mantener" : "mantener",
  };
}

function bmiClass(bmi: number): { label: string; tone: "muted" | "ok" | "warn" | "danger" } {
  if (bmi < 18.5) return { label: "Bajo peso", tone: "warn" };
  if (bmi < 25) return { label: "Normal", tone: "ok" };
  if (bmi < 30) return { label: "Sobrepeso", tone: "warn" };
  if (bmi < 35) return { label: "Obesidad I", tone: "danger" };
  if (bmi < 40) return { label: "Obesidad II", tone: "danger" };
  return { label: "Obesidad III", tone: "danger" };
}

function whrClass(sex: Sex, whr: number): { label: string; tone: "ok" | "warn" | "danger" } {
  if (sex === "M") {
    if (whr < 0.9) return { label: "Bajo riesgo", tone: "ok" };
    if (whr < 1.0) return { label: "Riesgo moderado", tone: "warn" };
    return { label: "Riesgo alto", tone: "danger" };
  }
  if (whr < 0.8) return { label: "Bajo riesgo", tone: "ok" };
  if (whr < 0.85) return { label: "Riesgo moderado", tone: "warn" };
  return { label: "Riesgo alto", tone: "danger" };
}

/** WHtR — cintura/estatura. Cortes universales (sirven para H y M, todas las
 *  edades). Más consistente con riesgo cardiometabólico real que WHR. Regla
 *  mnemotécnica: "tu cintura debe ser menor que la mitad de tu estatura". */
function whtrClass(whtr: number): { label: string; tone: "muted" | "ok" | "warn" | "danger" } {
  if (whtr < 0.4) return { label: "Bajo peso central", tone: "muted" };
  if (whtr < 0.5) return { label: "Saludable", tone: "ok" };
  if (whtr < 0.6) return { label: "Riesgo aumentado", tone: "warn" };
  return { label: "Riesgo muy alto", tone: "danger" };
}

function bfClass(sex: Sex, bf: number): { label: string; tone: "muted" | "ok" | "warn" | "danger" } {
  if (sex === "M") {
    if (bf < 6) return { label: "Esencial", tone: "warn" };
    if (bf < 14) return { label: "Atlético", tone: "ok" };
    if (bf < 18) return { label: "En forma", tone: "ok" };
    if (bf < 25) return { label: "Aceptable", tone: "warn" };
    return { label: "Obesidad", tone: "danger" };
  }
  if (bf < 14) return { label: "Esencial", tone: "warn" };
  if (bf < 21) return { label: "Atlético", tone: "ok" };
  if (bf < 25) return { label: "En forma", tone: "ok" };
  if (bf < 32) return { label: "Aceptable", tone: "warn" };
  return { label: "Obesidad", tone: "danger" };
}

interface Computed {
  bmi: number | null;
  bmr: number | null;
  tdee: number | null;
  caloriesGoal: number | null;
  bodyFat: number | null;
  whr: number | null;
  whtr: number | null;  // Cintura/altura (Waist-to-Height Ratio) — más moderno que WHR
}

function compute(inp: Inputs): Computed {
  const age = num(inp.age);
  const weight = num(inp.weight);
  const height = num(inp.height);
  const waist = num(inp.waist);
  const hip = num(inp.hip);
  const neck = num(inp.neck);
  const factor = ACTIVITY_FACTOR[inp.activity];

  const bmi = weight && height ? weight / Math.pow(height / 100, 2) : null;

  const bmr =
    weight && height && age
      ? inp.sex === "M"
        ? 10 * weight + 6.25 * height - 5 * age + 5
        : 10 * weight + 6.25 * height - 5 * age - 161
      : null;

  const tdee = bmr ? bmr * factor : null;

  const caloriesGoal =
    tdee == null ? null : inp.goal === "deficit" ? tdee - 400 : inp.goal === "superavit" ? tdee + 300 : tdee;

  // US Navy — Hodgdon-Beckett (fórmula correcta en CM, NO la imperial).
  // Antes usábamos la imperial 86.01·log10(...) + 36.76 con inputs en cm
  // → daba ~50% para mujer normal (verificado con Camila 162/79/34/83.9
  // daba 50.18%, correcto 23.50%).
  let bodyFat: number | null = null;
  if (inp.sex === "M") {
    if (waist && neck && height && waist - neck > 0) {
      const denom = 1.0324 - 0.19077 * Math.log10(waist - neck) + 0.15456 * Math.log10(height);
      if (denom > 0) bodyFat = 495 / denom - 450;
    }
  } else {
    if (waist && hip && neck && height && waist + hip - neck > 0) {
      const denom = 1.29579 - 0.35004 * Math.log10(waist + hip - neck) + 0.22100 * Math.log10(height);
      if (denom > 0) bodyFat = 495 / denom - 450;
    }
  }
  if (bodyFat != null && (bodyFat < 0 || bodyFat > 80)) bodyFat = null;

  const whr = waist && hip ? waist / hip : null;
  // WHtR — más consistente con riesgo cardiometabólico real que WHR.
  // Útil cuando WHR confunde por morfología androide/ginecoide (ver bfClass note).
  const whtr = waist && height ? waist / height : null;

  return { bmi, bmr, tdee, caloriesGoal, bodyFat, whr, whtr };
}

function fmt(n: number | null, digits = 1, suffix = ""): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return n.toFixed(digits) + suffix;
}

function fmtInt(n: number | null, suffix = ""): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return Math.round(n).toString() + suffix;
}

const TONE_CLASS: Record<string, string> = {
  ok: "chip",
  warn: "chip amber",
  danger: "chip magenta",
  muted: "chip",
};

export function MeasurementsScreen() {
  const toast = useToast();
  const [inp, setInp] = useState<Inputs>(DEFAULTS);

  // Herencia de cliente
  const [clients, setClients] = useState<ClientFull[] | null>(null);
  const [clientId, setClientId] = useState<number | null>(null);
  /** Snapshot del input que corresponde al cliente seleccionado, para detectar cambios. */
  const [baseline, setBaseline] = useState<Inputs | null>(null);
  const [saving, setSaving] = useState(false);

  // Cargar clientes activos una vez al montar.
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const list = await listClients({ status: "active" });
        if (!cancel) setClients(list);
      } catch {
        if (!cancel) setClients([]);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const selectedClient = useMemo(
    () => clients?.find((c) => c.id === clientId) || null,
    [clients, clientId],
  );

  // Cuando cambia el cliente seleccionado, pre-rellenar inputs.
  useEffect(() => {
    if (!selectedClient) return;
    const next = inputsFromClient(selectedClient);
    setInp(next);
    setBaseline(next);
  }, [selectedClient]);

  // ¿El coach modificó algún campo respecto al baseline del cliente?
  const dirty = useMemo(() => {
    if (!baseline) return false;
    return JSON.stringify(baseline) !== JSON.stringify(inp);
  }, [baseline, inp]);

  const out = useMemo(() => compute(inp), [inp]);
  const set = <K extends keyof Inputs>(k: K, v: Inputs[K]) => setInp((s) => ({ ...s, [k]: v }));

  const handleSelectClient = (id: string) => {
    if (id === "") {
      setClientId(null);
      setBaseline(null);
      setInp(DEFAULTS);
      return;
    }
    setClientId(Number(id));
  };

  /** Persiste los cambios hechos al cliente vinculado. */
  const handleSaveToClient = async () => {
    if (!selectedClient) return;
    setSaving(true);
    try {
      const patch = {
        sex: inp.sex,
        height_cm: num(inp.height),
        current_weight_kg: num(inp.weight),
        waist_cm: num(inp.waist),
        neck_cm: num(inp.neck),
        hip_cm: num(inp.hip),
        activity_level: ACTIVITY_TO_CLIENT[inp.activity],
        goal: GOAL_TO_CLIENT[inp.goal],
      };
      const saved = await updateClient(selectedClient.id, patch);
      // Refrescar lista local + baseline (queda "limpio" desde ahora)
      setClients((list) => (list ? list.map((c) => (c.id === saved.id ? saved : c)) : list));
      setBaseline(inp);
      toast(`Datos guardados en "${saved.full_name}"`);
    } catch (e) {
      toast("No pude guardar al cliente: " + (e instanceof Error ? e.message : ""));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page al-meas">
      <div className="al-meas-head">
        <div>
          <h1 className="al-page-title">Medidas y cálculos</h1>
          <p className="muted">
            IMC, TMB (Mifflin-St Jeor), TDEE, calorías objetivo, % grasa US Navy y cintura/cadera.
          </p>
        </div>
      </div>

      {/* ---------- Selector de cliente (herencia) ---------- */}
      <div className="al-meas-client-bar">
        <label className="al-meas-client-label">
          <Icon name="user" size={14} /> Calculando para:
        </label>
        <select
          className="al-meas-client-select"
          value={clientId == null ? "" : String(clientId)}
          onChange={(e) => handleSelectClient(e.target.value)}
        >
          <option value="">Sin cliente (manual)</option>
          {(clients || []).map((c) => (
            <option key={c.id} value={c.id}>{c.full_name}</option>
          ))}
        </select>
        {selectedClient && (
          <span className="al-meas-inherit-badge">
            Datos heredados de <b>{selectedClient.full_name}</b>
          </span>
        )}
        {selectedClient && dirty && (
          <button
            className="btn btn-primary btn-sm"
            onClick={handleSaveToClient}
            disabled={saving}
            title={`Persistir cambios en el perfil de ${selectedClient.full_name}`}
          >
            {saving ? "Guardando…" : "Guardar cambios al cliente"}
          </button>
        )}
      </div>

      <div className="al-meas-grid">
        {/* ---------- Form ---------- */}
        <div className="card al-meas-form">
          <div className="card-title">Datos</div>

          <div className="al-meas-row">
            <div className="field">
              <label>Sexo</label>
              <div className="al-meas-radio">
                <label>
                  <input
                    type="radio"
                    name="sex"
                    checked={inp.sex === "M"}
                    onChange={() => set("sex", "M")}
                  />{" "}
                  Hombre
                </label>
                <label>
                  <input
                    type="radio"
                    name="sex"
                    checked={inp.sex === "F"}
                    onChange={() => set("sex", "F")}
                  />{" "}
                  Mujer
                </label>
              </div>
            </div>
            <div className="field">
              <label>Edad</label>
              <input
                type="number"
                inputMode="numeric"
                placeholder="Años"
                value={inp.age}
                onChange={(e) => set("age", e.target.value)}
              />
            </div>
          </div>

          <div className="al-meas-row">
            <div className="field">
              <label>Peso (kg)</label>
              <input
                type="number"
                inputMode="decimal"
                value={inp.weight}
                onChange={(e) => set("weight", e.target.value)}
              />
            </div>
            <div className="field">
              <label>Estatura (cm)</label>
              <input
                type="number"
                inputMode="decimal"
                value={inp.height}
                onChange={(e) => set("height", e.target.value)}
              />
            </div>
          </div>

          <div className="al-meas-row">
            <div className="field">
              <label>Cintura (cm)</label>
              <input
                type="number"
                inputMode="decimal"
                value={inp.waist}
                onChange={(e) => set("waist", e.target.value)}
              />
            </div>
            <div className="field">
              <label>Cadera (cm)</label>
              <input
                type="number"
                inputMode="decimal"
                value={inp.hip}
                onChange={(e) => set("hip", e.target.value)}
              />
            </div>
          </div>

          <div className="al-meas-row">
            <div className="field">
              <label>Cuello (cm)</label>
              <input
                type="number"
                inputMode="decimal"
                value={inp.neck}
                onChange={(e) => set("neck", e.target.value)}
              />
              <span className="hint">Necesario para % grasa US Navy</span>
            </div>
            <div className="field">
              <label>Nivel de actividad</label>
              <select value={inp.activity} onChange={(e) => set("activity", e.target.value as Activity)}>
                {Object.entries(ACTIVITY_LABEL).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="al-meas-row">
            <div className="field">
              <label>Objetivo</label>
              <select value={inp.goal} onChange={(e) => set("goal", e.target.value as Goal)}>
                <option value="mantener">Mantener</option>
                <option value="deficit">Déficit (−400 kcal)</option>
                <option value="superavit">Superávit (+300 kcal)</option>
              </select>
            </div>
            <div className="field">
              <label>&nbsp;</label>
              <div className="al-meas-actions">
                <button
                  className="btn btn-ghost"
                  onClick={() => {
                    if (selectedClient && baseline) {
                      // Revertir al baseline del cliente
                      setInp(baseline);
                    } else {
                      setInp(DEFAULTS);
                    }
                  }}
                >
                  <Icon name="x" size={14} /> Reset
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => toast("Próximamente: aplicar al plan actual")}
                  disabled
                  title="Disponible próximamente"
                >
                  Aplicar al plan
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ---------- Resultados ---------- */}
        <div className="al-meas-results">
          <ResultCard
            label="IMC"
            value={fmt(out.bmi, 1)}
            unit="kg/m²"
            chip={out.bmi != null ? bmiClass(out.bmi) : null}
            help="Índice de masa corporal (OMS)"
          />
          <ResultCard
            label="TMB"
            value={fmtInt(out.bmr)}
            unit="kcal/día"
            help="Tasa metabólica basal — Mifflin-St Jeor"
          />
          <ResultCard
            label="TDEE"
            value={fmtInt(out.tdee)}
            unit="kcal/día"
            help={`TMB × ${ACTIVITY_FACTOR[inp.activity]} (${ACTIVITY_LABEL[inp.activity]})`}
          />
          <ResultCard
            label="Calorías objetivo"
            value={fmtInt(out.caloriesGoal)}
            unit="kcal/día"
            help={
              inp.goal === "deficit"
                ? "TDEE − 400 (pérdida ~0.4 kg/sem)"
                : inp.goal === "superavit"
                ? "TDEE + 300 (ganancia limpia)"
                : "Mantenimiento"
            }
          />
          <ResultCard
            label="% grasa corporal"
            value={fmt(out.bodyFat, 1)}
            unit="%"
            chip={out.bodyFat != null ? bfClass(inp.sex, out.bodyFat) : null}
            help="US Navy method (cintura, cuello, estatura)"
          />
          <ResultCard
            label="Cintura / cadera"
            value={fmt(out.whr, 2)}
            unit=""
            chip={out.whr != null ? whrClass(inp.sex, out.whr) : null}
            help="WHR — riesgo cardiovascular OMS"
          />
          <ResultCard
            label="Cintura / estatura"
            value={fmt(out.whtr, 2)}
            unit=""
            chip={out.whtr != null ? whtrClass(out.whtr) : null}
            help="WHtR — indicador moderno: tu cintura debe ser < ½ de tu estatura"
          />
        </div>
      </div>
    </div>
  );
}

interface ResultCardProps {
  label: string;
  value: string;
  unit: string;
  chip?: { label: string; tone: "muted" | "ok" | "warn" | "danger" } | null;
  help?: string;
}

function ResultCard({ label, value, unit, chip, help }: ResultCardProps) {
  return (
    <div className="card al-meas-card">
      <div className="stat-label">{label}</div>
      <div className="al-meas-value">
        <span className="stat-value">{value}</span>
        {unit && <span className="al-meas-unit">{unit}</span>}
      </div>
      {chip && <span className={TONE_CLASS[chip.tone]}>{chip.label}</span>}
      {help && <div className="tiny muted">{help}</div>}
    </div>
  );
}
